import { createQuery, Not, type Entity, type World } from 'koota';
import { deadlyFootprints } from '../../levels/validate';
import { getContext, type SimContext } from '../context';
import { cannonBall } from '../hazards/cannonTiming';
import { cloneInput, EMPTY_INPUT, type PlayerInput } from '../input';
import { raycastClosest, type RayHit } from '../physics/queries';
import { combatAllowed } from '../rules/rounds';
import { BOT_SKILL, BOT_STYLES } from '../rules/settings';
import { RESCUE_RADIUS, sameTeam } from '../rules/party';
import { weaponByIndex } from '../weapons/defs';
import { aimWeaponAt, weaponMuzzle, weaponPoint } from '../weapons/geometry';
import { resolveWeaponAnchor } from '../weapons/pose';
import type { WeaponDef } from '../weapons/schema';
import {
  Aim,
  Anchor,
  Bot,
  Combat,
  Controller,
  Dead,
  Hazard,
  HazardKind,
  Health,
  Held,
  HeldBy,
  Loose,
  OwnedBy,
  Player,
  PartyPlayer,
  PartyState,
  Projectile,
  Shape,
  Transform,
  Weapon,
} from '../traits';
import {
  navFor,
  predictLanding,
  routeStep,
  surfaceBelow,
  surfaceUnderFeet,
  type NavGraph,
  type MovingSaw,
} from './nav';

export const BotMode = { Hunt: 0, Fetch: 1, Recover: 2, Objective: 3, Rescue: 4 } as const;

const bots = createQuery(Player, Controller, Transform, Aim);
const livePlayers = createQuery(Player, Transform, Not(Dead));
const looseWeapons = createQuery(Weapon, Loose, Transform);

type Fighter = {
  e: Entity;
  slot: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  hp: number;
  surf: number;
};
type Pickup = { e: Entity; x: number; y: number; def: WeaponDef; surf: number };
type Shot = { x: number; y: number; vx: number; vy: number; owner: Entity | undefined };
type RouteProgress = { from: number; to: number; launchX: number; remaining: number };
/** A laser emitter: the beam runs right from (x, y) for `reach`; `warn` is ticks until it fires (0 while firing). */
type Beam = { x: number; y: number; reach: number; armed: boolean; warn: number };

const hazards = createQuery(Hazard, Transform);

const DEADLY = new Set<number>([
  HazardKind.Lava,
  HazardKind.Spikes,
  HazardKind.Saw,
  HazardKind.Spikeball,
  HazardKind.Laser,
  HazardKind.Crusher,
]);

const clamp1 = (v: number) => Math.max(-1, Math.min(1, v));

export function attachBots(
  world: World,
  slots: number[],
  skill = BOT_SKILL[getContext(world).settings.botDifficulty],
): void {
  world.query(Player).updateEach(([p], e) => {
    if (!slots.includes(p.slot) || e.has(Bot)) return;
    const style = BOT_STYLES.indexOf(
      getContext(world).settings.botStyles[p.slot] ?? BOT_STYLES[p.slot % BOT_STYLES.length]!,
    );
    e.add(
      Bot({ slot: p.slot, style, think: p.slot * 7, skill, strafe: p.slot % 2 === 0 ? 1 : -1 }),
    );
  });
}

function heldWeapon(
  world: World,
  player: Entity,
): { def: WeaponDef; defId: number; ammo: number } | null {
  for (const weapon of world.query(Weapon, Held)) {
    if (weapon.targetFor(HeldBy) !== player) continue;
    const w = weapon.get(Weapon);
    if (!w) return null;
    return { def: weaponByIndex(w.defId), defId: w.defId, ammo: w.ammo };
  }
  return null;
}

function isDeadly(world: World, hit: RayHit | null): boolean {
  if (!hit || hit.kind !== 'hazard') return false;
  const e = hit.entity as Entity;
  if (!world.has(e)) return false;
  const hz = e.get(Hazard);
  return !!hz && DEADLY.has(hz.kind);
}

/** Static/prop geometry only: players, ragdolls, weapons, sensors and bullets never count as ground or walls. */
function solidOnly(self: Entity) {
  return (h: RayHit) =>
    h.entity === (self as unknown as number) ||
    h.kind === 'sensor' ||
    h.kind === 'projectile' ||
    h.kind === 'weapon' ||
    h.kind === 'player' ||
    h.kind === 'ragdoll';
}

/** Preferred engagement band for a weapon; melee wants to be in punching range. */
function rangeBand(def: WeaponDef | null): { min: number; max: number } {
  if (!def) return { min: 0, max: 1.1 };
  if (def.throwProfile?.attackThrows)
    return { min: 0.7, max: def.throwProfile.upright ? 4.5 : def.throwProfile.speed * 0.27 };
  if (def.projectile.kind === 'melee') {
    // Blades reach a little past the fist; the blink knife closes its own gap.
    if (def.id === 'blink-dagger') return { min: 1, max: 4 };
    return { min: 0, max: 0.5 + def.projectile.radius };
  }
  switch (def.projectile.kind) {
    case 'return-disc':
      return { min: 1.5, max: 6.5 };
    case 'siphon':
      return { min: 1, max: 3.2 };
    case 'pellets':
      return { min: 1.2, max: 4.5 };
    case 'grenade':
      return def.projectile.bounce === 0 ? { min: 1.5, max: 5 } : { min: 4, max: 10 };
    case 'burst-into':
      return { min: 4, max: 10 };
    case 'rocket':
      return { min: 4.5, max: 12 };
    case 'beam':
      return { min: 2, max: 9 };
    case 'creature':
      return { min: 2.5, max: 8 };
    case 'field':
      // Sprays are short; the anchored fields want to be lobbed from a safe distance.
      return def.id === 'black-hole' || def.id === 'time-bubble'
        ? { min: 5, max: 10 }
        : { min: 1.5, max: 4 };
    default:
      return def.id === 'sniper' ? { min: 5, max: 16 } : { min: 2.5, max: 9 };
  }
}

function collect(world: World, ctx: SimContext, nav: NavGraph) {
  const half = ctx.tuning.height / 2;
  const fighters: Fighter[] = [];
  world.query(livePlayers).updateEach(([p, tr], e) => {
    const body = ctx.bodies.get(e);
    const v = body?.getLinearVelocity();
    fighters.push({
      e,
      slot: p.slot,
      x: tr.x,
      y: tr.y,
      vx: v?.x ?? 0,
      vy: v?.y ?? 0,
      hp: e.get(Health)?.hp ?? 0,
      surf: surfaceBelow(nav, tr.x, tr.y - half),
    });
  });
  const pickups: Pickup[] = [];
  world.query(looseWeapons).updateEach(([w, tr], e) => {
    if (w.pickupCooldown > 0) return;
    pickups.push({
      e,
      x: tr.x,
      y: tr.y,
      def: weaponByIndex(w.defId),
      surf: surfaceBelow(nav, tr.x, tr.y - 0.2, 2.5),
    });
  });
  const shots: Shot[] = [];
  world.query(Projectile).updateEach(([p], e) => {
    if (Math.hypot(p.vx, p.vy) < 4) return;
    shots.push({ x: p.x, y: p.y, vx: p.vx, vy: p.vy, owner: e.targetFor(OwnedBy) });
  });
  const beams: Beam[] = [];
  world.query(hazards).updateEach(([hz, tr]) => {
    if (hz.kind === HazardKind.Cannon) {
      const ball = cannonBall(hz, tr.x, tr.y, ctx.tick);
      if (ball) shots.push({ ...ball, owner: undefined });
      return;
    }
    if (hz.kind !== HazardKind.Laser) return;
    // Mirrors laser.step(): unarmed for param0 ticks, then firing for param1, offset by param2.
    const off = hz.param0 || 90;
    const phase = (ctx.tick + (hz.param2 || 0)) % (off + (hz.param1 || 90));
    beams.push({
      x: tr.x,
      y: tr.y,
      reach: hz.param3 || 14,
      armed: phase > off,
      warn: phase > off ? 0 : off + 1 - phase,
    });
  });
  return { fighters, pickups, shots, beams };
}

export function thinkBots(world: World): void {
  const ctx = getContext(world);
  const t = ctx.tuning;
  const rng = ctx.rng;
  const live = combatAllowed(world);
  const bounds = ctx.level.bounds;
  const nav = navFor(ctx.level);
  const saws: MovingSaw[] = [];
  for (const e of world.query(Hazard, Anchor, Shape)) {
    const h = e.get(Hazard)!;
    if (h.kind !== HazardKind.Saw || h.param1 <= 0) continue;
    saws.push({ ...e.get(Anchor)!, r: Math.max(0.5, e.get(Shape)!.r), span: h.param1, speed: Math.max(0.1, h.param2), phase: h.param3 });
  }
  // The route graph reserves a saw's whole path. Local jumps can use a safe time window;
  // treating the entire sweep as permanently occupied traps fighters at low conveyor lips.
  const flightNav = saws.length ? {
    ...nav,
    deadly: deadlyFootprints({
      ...ctx.level,
      objects: ctx.level.objects.filter((o) =>
        o.type !== 'saw' || !o.path?.[1] || o.path[0]!.x === o.path[1].x),
    }),
  } : nav;
  let scene: ReturnType<typeof collect> | null = null;

  world.query(bots).updateEach(([player, ctrl, tr], entity) => {
    if (!entity.has(Bot) || entity.has(Dead)) return;
    const bot = entity.get(Bot);
    const body = ctx.bodies.get(entity);
    if (!bot || !body) return;
    scene ??= collect(world, ctx, nav);
    const { fighters, pickups, shots, beams } = scene;

    bot.think += 1;
    if (bot.timer > 0) bot.timer -= 1;
    if (bot.shunTicks > 0) bot.shunTicks -= 1;
    if (bot.reactTicks > 0) bot.reactTicks -= 1;
    if (bot.attackWait > 0) bot.attackWait -= 1;
    if (bot.engagementGrace > 0) bot.engagementGrace -= 1;
    if (bot.blockTicks > 0) bot.blockTicks -= 1;
    if (bot.throwArmed > 0) bot.throwArmed -= 1;
    if (bot.detour > 0) bot.detour -= 1;
    if (bot.avoidTicks > 0) bot.avoidTicks -= 1;
    bot.strafeTicks -= 1;
    if (bot.strafeTicks <= 0) {
      bot.strafe = rng.next() < 0.5 ? -1 : 1;
      bot.strafeTicks = 30 + rng.nextInt(50);
    }
    if (bot.think % 18 === 0) bot.aimErr = (rng.next() - 0.5) * (1 - bot.skill) * 0.9;

    const input: PlayerInput = cloneInput(EMPTY_INPUT);
    const px = tr.x;
    const py = tr.y;
    const feetY = py - t.height / 2;
    const v = body.getLinearVelocity();
    const skip = solidOnly(entity);
    const held = heldWeapon(world, entity);
    const def = held?.def ?? null;
    const ranged = !!def && def.projectile.kind !== 'melee';
    const outOfAmmo =
      ctx.settings.mode !== 'arms-race' && !!held && !held.def.infiniteAmmo && held.ammo <= 0;
    // Fetching another weapon requires an empty hand. Discard the dry gun even when the
    // enemy is far away; waiting for throwing range can otherwise trap Fetch mode forever.
    if (live && outOfAmmo && bot.throwArmed === 0) {
      input.throw = true;
      bot.throwArmed = 30;
    }

    // Where am I in the nav graph? Non-static platforms (movers, crumblers) are not nodes, so a
    // grounded bot with no node under it navigates by sight instead.
    let here = surfaceUnderFeet(nav, px, feetY);
    // A corpse or crate can lift our feet above the static floor without changing which
    // platform we are on. Keep routing from that floor while crossing the pile.
    if (ctrl.grounded && here < 0) {
      const support = raycastClosest(world, px, py, px, feetY - 0.2,
        (h) => skip(h) && h.kind !== 'ragdoll');
      if (support?.kind === 'ragdoll' || support?.kind === 'prop')
        here = surfaceBelow(nav, px, feetY, 3.2);
    }
    if (ctrl.grounded && here >= 0) bot.surf = here;
    const mySurf = ctrl.grounded ? here : bot.surf;
    const reachable = (surf: number) =>
      surf < 0 || mySurf < 0 || surf === mySurf || routeStep(nav, mySurf, surf) !== null;

    // --- Targeting: stick to the current enemy unless another is decisively closer; prefer
    // enemies we can actually walk to.
    const enemies = fighters.filter((f) => f.e !== entity && !sameTeam(world, entity, f.e));
    const dist2 = (f: Fighter) =>
      ((f.x - px) ** 2 + (f.y - py) ** 2) * (bot.style === 2 ? 0.3 + f.hp / ctx.settings.maxHp : 1);
    let target = enemies.find((f) => f.slot === bot.target) ?? null;
    const pool = enemies.filter((f) => reachable(f.surf));
    const candidates = pool.length > 0 && !ranged ? pool : enemies;
    const nearest = candidates.reduce<Fighter | null>(
      (best, f) => (!best || dist2(f) < dist2(best) ? f : best),
      null,
    );
    if (!target || (nearest && bot.think % 30 === 0 && dist2(nearest) < dist2(target) * 0.5))
      target = nearest;
    if (target && !ranged && !reachable(target.surf) && nearest && reachable(nearest.surf))
      target = nearest;
    if (bot.target !== (target?.slot ?? -1) || bot.weaponDef !== (held?.defId ?? -1)) {
      bot.engaged = false;
      bot.engagementGrace = 0;
    }
    bot.target = target?.slot ?? -1;
    bot.weaponDef = held?.defId ?? -1;

    // --- Weapon fetching: unarmed (or dry) bots go for the nearest reachable gun.
    let pickup: Pickup | null = null;
    if (ctx.settings.mode !== 'arms-race' && (!held || outOfAmmo)) {
      let bestScore = Infinity;
      for (const p of pickups) {
        const dx = p.x - px;
        const dy = p.y - py;
        if (Math.abs(dx) > 16 || dy > 6 || dy < -9) continue;
        if (bot.shunTicks > 0 && (p.e as unknown as number) === bot.shunned) continue;
        if (!reachable(p.surf)) continue;
        const under = raycastClosest(world, p.x, p.y + 0.2, p.x, p.y - 1.5, skip);
        if (!under || isDeadly(world, under)) continue;
        const score = Math.abs(dx) + Math.abs(dy) * 1.5 + (p.def.projectile.rare ? -3 : 0);
        if (score < bestScore) {
          bestScore = score;
          pickup = p;
        }
      }
    }

    let moveX = 0;
    let wantJump = false;
    let aimX = ctrl.facing;
    let aimY = 0;
    let attack = false;
    let engaged = false;
    let navigating = false;
    let climbing = false;
    let route: RouteProgress | null = null;

    /** Follow the surface graph toward a goal on another surface. Returns false when no route exists. */
    const navigate = (goalSurf: number): boolean => {
      if (mySurf < 0 || goalSurf < 0 || goalSurf === mySurf) return false;
      const avoided = bot.avoidTicks > 0 ? { from: bot.avoidFrom, to: bot.avoidTo } : undefined;
      // Adjacent floor/conveyor nodes can share the same physical jump. Excluding just one
      // edge would send a bot into the same low ceiling through the other edge indefinitely.
      // Filter only this bot's failed launch, keeping clear opposite-side approaches available.
      const routes = avoided ? {
        ...nav,
        hops: nav.hops.map((row, from) => from !== avoided.from ? row : row.map((hop) => {
          if (!hop) return null;
          const blocked = Math.abs(hop.launchX - bot.avoidLaunchX) < t.radius * 2;
          const altBlocked = !hop.alt || Math.abs(hop.alt.launchX - bot.avoidLaunchX) < t.radius * 2;
          if (blocked) return altBlocked ? null : hop.alt!;
          return hop.alt && altBlocked ? { ...hop, alt: undefined } : hop;
        })),
      } : nav;
      let step = routeStep(routes, mySurf, goalSurf, avoided);
      // Some ledges have only one graph edge but two physical approaches. If the left launch
      // is obstructed, commit to the right one even when it is farther away (and vice versa).
      let alternate = false;
      if (!step && avoided) {
        const original = routeStep(nav, mySurf, goalSurf);
        if (original?.hop.alt && mySurf === avoided.from && original.to === avoided.to) {
          step = original;
          alternate = true;
        }
      }
      if (!step) return false;
      // Ledges climbable from either side: take the approach nearest to where we stand.
      const hop =
        step.hop.alt && (alternate
          ? Math.abs(step.hop.launchX - bot.avoidLaunchX) < Math.abs(step.hop.alt.launchX - bot.avoidLaunchX)
          : Math.abs(step.hop.alt.launchX - px) < Math.abs(step.hop.launchX - px))
          ? step.hop.alt
          : step.hop;
      navigating = true;
      route = {
        from: mySurf,
        to: step.to,
        launchX: hop.launchX,
        remaining: ctrl.grounded
          ? Math.abs(hop.launchX - px) + Math.hypot(hop.landX - hop.launchX, hop.rise)
          : Math.hypot(hop.landX - px, nav.surfaces[step.to]!.top - feetY),
      };
      if (ctrl.grounded) {
        const d = hop.launchX - px;
        const jumpDir = Math.sign(hop.landX - hop.launchX) || ctrl.facing;
        // Arriving at speed, take off a few ticks early: the jump is what stops us overshooting the
        // launch spot into whatever the gap holds.
        const closing = Math.sign(v.x) === jumpDir ? (Math.abs(v.x) * 3) / t.tickRate : 0;
        if (Math.abs(d) > 0.3 + closing) {
          moveX = clamp1(d * 2);
        } else {
          moveX = jumpDir;
          if (hop.rise > 0.05 || Math.abs(hop.landX - hop.launchX) > 0.9) wantJump = true;
        }
      } else {
        const d = hop.landX - px;
        moveX = Math.abs(d) < 0.15 ? 0 : clamp1(d * 2);
        if (hop.wall) {
          wantJump = true;
          climbing = true;
        }
      }
      return true;
    };

    const offStage = px < bounds.x + 0.4 || px > bounds.x + bounds.w - 0.4 || py < bounds.y + 1.2;
    if (offStage) {
      bot.mode = BotMode.Recover;
      moveX = Math.sign(bounds.x + bounds.w / 2 - px) || 1;
      wantJump = true;
      climbing = true;
    } else if (
      pickup &&
      (!target ||
        bot.mode === BotMode.Fetch ||
        Math.abs(pickup.x - px) < Math.hypot(target.x - px, target.y - py) * 1.6)
    ) {
      if (bot.mode !== BotMode.Fetch) {
        bot.mode = BotMode.Fetch;
        bot.timer = 300;
      }
      if (!navigate(pickup.surf)) {
        const dx = pickup.x - px;
        const dy = pickup.y - py;
        moveX = Math.abs(dx) < 0.12 ? 0 : clamp1(dx * 2.5);
        if (dy > 0.6 && Math.abs(dx) < 3 && ctrl.grounded) wantJump = true;
        // Standing over a gun that is out of reach below (buried under a corpse pile, wedged under
        // a lip): staring at it is not a plan.
        if (Math.abs(dx) < 0.35 && dy < -0.95) bot.timer = Math.min(bot.timer, 20);
      }
      if (target) {
        aimX = target.x - px;
        aimY = target.y - py;
      }
      if (bot.timer === 0) {
        // Couldn't get it: write this one off for a while and go fight.
        bot.mode = BotMode.Hunt;
        bot.shunned = pickup.e as unknown as number;
        bot.shunTicks = 600;
      }
    } else if (target) {
      bot.mode = BotMode.Hunt;
      const dx = target.x - px;
      const dy = target.y - py;
      const dist = Math.hypot(dx, dy);
      const band = rangeBand(def);
      if (ranged && bot.style === 0) {
        band.min *= 0.7;
        band.max *= 0.8;
      }
      if (ranged && bot.style === 1) {
        band.min *= 1.5;
        band.max *= 1.2;
      }
      // Finite reach and charges apply to every style, including its usual firing margin.
      const rangeLimit = def?.throwProfile?.attackThrows
        ? band.max
        : def?.projectile.kind === 'siphon' || def?.projectile.kind === 'return-disc'
          ? def.projectile.radius
        : def?.projectile.kind === 'bullet' && def.projectile.fuse > 0
          ? def.projectile.speed * def.projectile.fuse / t.tickRate * 0.85
          : Infinity;
      band.max = Math.min(band.max, rangeLimit);
      const attackRange = Math.min(band.max * 1.4, rangeLimit);
      const blastClearance = def && def.projectile.explodeDamage >= 60 ? def.projectile.radius + 1 : 0;
      // Aggression may shorten rifle range, but must never move a launcher inside its own blast.
      band.min = Math.max(band.min, blastClearance + (blastClearance ? 0.4 : 0));
      const toward = Math.sign(dx) || ctrl.facing;
      const clear = ranged ? raycastClosest(world, px, py + 0.1, target.x, target.y, skip) : null;
      const los = !ranged || !clear || clear.fraction > 0.92;
      const inBand = ranged && los && dist >= band.min && dist <= band.max;

      if (inBand) {
        moveX = bot.style === 0 ? toward * 0.3 : bot.strafe * (bot.style === 1 ? 0.2 : 0.45);
      } else if (ranged && los && dist < band.min) {
        moveX = -toward * 0.9;
      } else if (!navigate(target.surf)) {
        if (mySurf >= 0 && target.surf >= 0 && target.surf !== mySurf) {
          // No route: hold this surface and wait for them (or a gun drop) rather than pace at a ledge.
          const s = nav.surfaces[mySurf]!;
          const want = Math.max(s.x1 + 0.6, Math.min(s.x2 - 0.6, target.x));
          moveX = Math.abs(want - px) < 0.3 ? 0 : clamp1((want - px) * 2);
        } else if (!ranged) {
          // Fists: close in, but stop short so the punch lands instead of walking through them.
          moveX = Math.abs(dx) < 0.7 ? 0 : clamp1(dx / 1.2);
          if (Math.abs(dx) < 0.7 && Math.abs(dy) > 1) moveX = bot.strafe * 0.6;
          if (dy > 1.1 && Math.abs(dx) < 4.5 && ctrl.grounded) wantJump = true;
        } else {
          moveX = toward;
          if (dy > 1.1 && Math.abs(dx) < 4.5 && ctrl.grounded) wantJump = true;
        }
      }

      // Aim with lead; lob gravity projectiles.
      let lead = 0;
      let lob = 0;
      const thrown = def?.throwProfile?.attackThrows ? def.throwProfile : null;
      if (ranged && def) {
        const speed = Math.max(8, thrown?.speed ?? def.projectile.speed);
        lead = def.projectile.kind === 'siphon' ? 0 : dist / speed;
        // Chairs roll toward targets on the same floor. Fragile throws need an arc and inherit
        // the thrower's velocity, unlike bullets launched from a gun muzzle.
        const gravity = thrown ? thrown.upright && Math.abs(dy) < 1 ? 0 : t.gravity : def.projectile.gravity;
        if (gravity > 0)
          lob =
            0.5 *
            gravity *
            (ctx.settings.mutators.includes('low-gravity') ? 0.55 : 1) *
            lead *
            lead;
      }
      aimX = dx + target.vx * lead * bot.skill - (thrown ? v.x * lead : 0);
      aimY = dy + target.vy * lead * bot.skill + lob - (thrown ? v.y * lead : 0);
      if (ranged && def && !thrown) {
        const direction = aimWeaponAt(
          { x: px, y: py, ducking: ctrl.ducking, height: t.height },
          def,
          { x: px + aimX, y: py + aimY },
        );
        aimX = direction.x;
        aimY = direction.y;
      }
      const err = bot.aimErr;
      const ca = Math.cos(err);
      const sa = Math.sin(err);
      const rx = aimX * ca - aimY * sa;
      const ry = aimX * sa + aimY * ca;
      aimX = rx;
      aimY = ry;

      const reach = def && !ranged ? rangeBand(def).max + 0.4 : t.punchRange + 0.55;
      engaged = live && (ranged ? !outOfAmmo && dist < attackRange && los : dist < reach);
      // Start a new target's reaction window only once it can be attacked. Short occlusions
      // retain that tracking, so hopping past a ledge does not restart the window forever.
      // Standard gets about 0.6 s to line up a new attack; Expert remains quick at about 0.15 s.
      if (engaged && !bot.engaged && bot.engagementGrace === 0)
        bot.attackWait = Math.round(
          (6 + (1 - bot.skill) * 54) * (bot.style === 0 ? 0.85 : bot.style === 1 ? 1.1 : 1),
        );
      if (engaged && bot.attackWait === 0) {
        if (!ranged) {
          const cadence = Math.ceil(
            (def ? Math.max(6, def.fireIntervalTicks) : Math.max(6, t.punchCooldownTicks)) *
              (1 + (1 - bot.skill) * 1.8),
          );
          if (dist < reach && bot.think % cadence < 2) attack = true;
        } else if (def && !outOfAmmo && dist < attackRange && los) {
          const semi = def.fireMode === 'semi' || def.fireMode === 'burst';
          const interval = Math.ceil(
            Math.max(semi ? 6 : 1, def.fireIntervalTicks) * (1 + (1 - bot.skill) * 1.8),
          );
          const cycle = Math.round(90 - bot.skill * 30);
          attack = semi ? bot.think % interval < 2 : bot.think % cycle < 12 + bot.skill * 42;
        }
      }
      if (attack && def && blastClearance > 0) {
        attack = dist >= blastClearance;
        if (attack) {
          const anchor = resolveWeaponAnchor(world, entity, def, { x: px, y: py, aimX, aimY, ducking: ctrl.ducking, twoHanded: def.twoHanded, height: t.height });
          const muzzle = weaponPoint(weaponMuzzle(def.shape.kind, def.shape.length), anchor);
          const wall = raycastClosest(world, muzzle.x, muzzle.y, muzzle.x + aimX * blastClearance, muzzle.y + aimY * blastClearance, skip);
          if (wall && Math.hypot(wall.x - px, wall.y - py) < blastClearance) attack = false;
        }
      }
    } else {
      bot.mode = BotMode.Hunt;
      moveX = 0;
    }

    // Teammate rescue and the control point are movement goals, not enemy targets.
    // Keep aiming at opponents while approaching, then devote the guard input to rescuing.
    if (live && !offStage) {
      const downed =
        ctx.settings.mode === 'shared-stock'
          ? ctx.players.filter(
              (ally) =>
                ally.has(Dead) &&
                sameTeam(world, entity, ally) &&
                (ally.get(PartyPlayer)?.respawnTicks ?? 0) === 0,
            )
          : [];
      downed.sort((a, b) => {
        const ap = a.get(Transform)!,
          bp = b.get(Transform)!;
        return Math.hypot(ap.x - px, ap.y - py) - Math.hypot(bp.x - px, bp.y - py);
      });
      const ally = downed[0];
      if (ally) {
        route = null;
        navigating = climbing = wantJump = false;
        const pos = ally.get(Transform)!;
        const distance = Math.hypot(pos.x - px, pos.y - py);
        bot.mode = BotMode.Rescue;
        if (distance <= RESCUE_RADIUS * 0.85) {
          moveX = 0;
          input.block = true;
          attack = false;
        } else if (!navigate(surfaceBelow(nav, pos.x, pos.y - t.height / 2))) {
          moveX = clamp1((pos.x - px) * 2);
          if (pos.y > py + 0.7 && ctrl.grounded) wantJump = true;
        }
      } else if (ctx.settings.mode === 'king-of-hill') {
        route = null;
        navigating = climbing = wantJump = false;
        const hill = world.get(PartyState)!;
        const distance = Math.hypot(hill.hillX - px, hill.hillY - py);
        bot.mode = BotMode.Objective;
        if (distance > hill.hillRadius * 0.55) {
          if (!navigate(surfaceBelow(nav, hill.hillX, hill.hillY - t.height / 2))) {
            moveX = clamp1((hill.hillX - px) * 2);
            if (hill.hillY > py + 0.7 && ctrl.grounded) wantJump = true;
          }
        } else moveX = 0;
      }
    }

    // --- Countdown: hold position (the round isn't live; no reason to sprint onto a hazard).
    if (!live && !offStage) {
      moveX = 0;
      wantJump = false;
    }

    // --- Shoved into something the nav probes ignore (a corpse against a wall, a stacked
    // fighter, a crate): running flat out and not moving for a fifth of a second means hop it.
    const shoving =
      live && ctrl.grounded && Math.abs(moveX) > 0.5 && Math.abs(v.x) < 0.6 && !wantJump;
    bot.blocked = shoving ? bot.blocked + 1 : 0;
    if (bot.blocked >= 12) {
      wantJump = true;
      bot.blocked = 0;
    }

    // --- Route progress: a jump that bonks the same ceiling over and over is still stuck.
    // Remember the failed approach long enough to reach a farther launch or another graph path.
    const progress = route as RouteProgress | null;
    if (live && !offStage && progress && bot.detour === 0) {
      if (bot.progressFrom !== progress.from || bot.progressTo !== progress.to ||
          bot.progressBest < 0 || progress.remaining < bot.progressBest - 0.35) {
        bot.progressFrom = progress.from;
        bot.progressTo = progress.to;
        bot.progressBest = progress.remaining;
        bot.progressTicks = 0;
      } else if (++bot.progressTicks >= 90) {
        bot.avoidFrom = progress.from;
        bot.avoidTo = progress.to;
        bot.avoidLaunchX = progress.launchX;
        bot.avoidTicks = 360;
        bot.detour = 40;
        bot.detourDir = -(Math.sign(moveX) || bot.strafe);
        bot.progressBest = -1;
        bot.progressTicks = 0;
      }
    } else {
      bot.progressBest = -1;
      bot.progressTicks = 0;
    }

    // Without a graph route, lack of horizontal progress still calls for a brief retreat.
    // Bouncing on the spot cannot reset it; deliberate wall climbs can progress vertically.
    if (bot.think % 90 === 0) {
      const moved = climbing ? Math.hypot(px - bot.stuckX, py - bot.stuckY) : Math.abs(px - bot.stuckX);
      if (live && !navigating && moveX !== 0 && moved < 0.5 && bot.detour === 0) {
        bot.detour = 40;
        bot.detourDir = -(Math.sign(moveX) || bot.strafe);
      }
      bot.stuckX = px;
      bot.stuckY = py;
    }
    if (bot.detour > 0 && !offStage) {
      moveX = bot.detourDir;
      wantJump = ctrl.grounded && bot.detour > 30;
      navigating = climbing = false;
    }

    // --- Local navigation: walls, ledges, deadly floors, and stacked players.
    let wallAhead = false;
    if (moveX !== 0) {
      const dir = Math.sign(moveX);
      const obstacleSkip = (h: RayHit) => skip(h) && h.kind !== 'ragdoll';
      const wallHi = raycastClosest(world, px, py + 0.3, px + dir * 0.75, py + 0.3, obstacleSkip);
      const wallLo = raycastClosest(world, px, py - 0.6, px + dir * 0.75, py - 0.6, obstacleSkip);
      wallAhead = !!(wallHi || wallLo);
      if (wallAhead && ctrl.grounded) wantJump = true;
      const aheadX = px + dir * 1.0;
      const ledge = raycastClosest(world, aheadX, py, aheadX, py - 2.6, skip);
      const deadlyAhead = ledge !== null && isDeadly(world, ledge);
      // On a route the graph already placed the launch point before any spike strip; a sweeping
      // saw that wandered underfoot is hopped rather than strolled into.
      if (navigating && deadlyAhead && ctrl.grounded) wantJump = true;
      // Recovering from off-stage, the only way is back in: never let the ledge check turn us round.
      if (!navigating && !offStage) {
        if (!ledge || deadlyAhead) {
          const deep = ledge ?? raycastClosest(world, aheadX, py, aheadX, bounds.y - 2, skip);
          const dangerous = !deep || isDeadly(world, deep);
          const goalAcross =
            (target &&
              bot.mode === BotMode.Hunt &&
              Math.sign(target.x - px) === dir &&
              Math.abs(target.x - px) > 1.4 &&
              target.y > py - 1.5) ||
            (bot.mode === BotMode.Fetch &&
              pickup &&
              Math.sign(pickup.x - px) === dir &&
              pickup.y > py - 1.5);
          if (ctrl.grounded && goalAcross && !dangerous) {
            wantJump = true;
          } else if (dangerous) {
            moveX = -dir * 0.5;
            bot.strafe = -dir;
            bot.strafeTicks = 40;
          }
        }
      }
    }
    for (const other of fighters) {
      if (other.e === entity) continue;
      const dx = other.x - px;
      const dy = other.y - py;
      if (Math.abs(dx) < 0.75 && Math.abs(dy) > 0.9 && Math.abs(dy) < 2.4) {
        moveX = bot.strafe;
        if (dy < 0 && ctrl.grounded) wantJump = true;
      }
    }

    // --- In flight: look where this arc comes down. Chasing a target that doubled back mid-jump
    // must not end on a spike strip or in the void when steering the other way (or coasting) lands
    // on something solid.
    // --- Lasers: the beam kills at chest height only, so one standing in it is hopped just as it
    // fires (the airtime outlasts the burst); beams firing within a flight's duration are kill
    // bands for the arc checks below.
    const bands = beams
      .filter((b) => b.armed || b.warn < 60)
      .map((b) => ({ x1: b.x - 0.3, x2: b.x + b.reach + 0.3, y1: b.y - 0.45, y2: b.y + 0.45 }));
    if (live && ctrl.grounded) {
      for (const b of beams) {
        if (px < b.x - 0.3 || px > b.x + b.reach + 0.3 || Math.abs(py - b.y) > 0.55) continue;
        const reaction = Math.round(3 + (1 - bot.skill) * 10);
        if (!b.armed && b.warn <= reaction) wantJump = true;
        else if (b.armed) wantJump = true;
      }
    }

    if (!ctrl.grounded && !climbing && !offStage) {
      const lands = (dir: number) => {
        const land = predictLanding(flightNav, px, feetY, v.x, v.y, dir, t, bands, saws);
        if (land.deadly || land.surf < 0) return false;
        const under = raycastClosest(world, land.x, land.feet + 0.5, land.x, land.feet - 0.3, skip);
        return !isDeadly(world, under);
      };
      if (!lands(moveX)) {
        const flip = -(Math.sign(moveX) || Math.sign(v.x) || ctrl.facing);
        if (lands(flip)) moveX = flip;
        else if (moveX !== 0 && lands(0)) moveX = 0;
      }
    } else if (wantJump && ctrl.grounded && !offStage) {
      // Don't leap head-first into a saw hanging over this spot, or through a beam about to fire;
      // walk on and jump once clear. A beam through our own chest is the exception: that jump is
      // the escape.
      const inBeam = bands.some((b) => px > b.x1 && px < b.x2 && py > b.y1 && py < b.y2);
      if (!inBeam && predictLanding(flightNav, px, feetY, v.x, t.jumpSpeed, moveX, t, bands, saws).deadly) {
        wantJump = false;
        // Something the graph does not know about (a crate, a breakable block) stands between us
        // and a pit, and a full-push hop over it would carry us into the pit. A gentle hop that
        // comes down on or just past the obstacle is fine; without it the bot runs into the block
        // until the watchdog turns it round, forever.
        if (wallAhead) {
          moveX = Math.sign(moveX) * 0.25;
          wantJump = !predictLanding(flightNav, px, feetY, v.x, t.jumpSpeed, moveX, t, bands, saws).deadly;
        }
      }
    }

    // --- Defense: block bullets that are about to arrive; rarely guess at point-blank punches.
    let threat: Shot | null = null;
    for (const s of shots) {
      if (s.owner === entity || sameTeam(world, entity, s.owner)) continue;
      const rx = px - s.x;
      const ry = py - s.y;
      const sp = Math.hypot(s.vx, s.vy);
      const along = (rx * s.vx + ry * s.vy) / sp;
      if (along < 0 || along > 9) continue;
      const perp = Math.abs(rx * s.vy - ry * s.vx) / sp;
      if (perp < 1.1) {
        threat = s;
        break;
      }
    }
    bot.threatTicks = threat ? bot.threatTicks + 1 : 0;
    if (
      threat &&
      bot.threatTicks >= Math.round(4 + (1 - bot.skill) * 24) &&
      bot.blockTicks === 0 &&
      bot.reactTicks === 0
    ) {
      bot.reactTicks = Math.round(24 + (1 - bot.skill) * 48);
      if (rng.next() < bot.skill * (bot.style === 1 ? 0.95 : bot.style === 0 ? 0.4 : 0.65))
        bot.blockTicks = bot.style === 1 ? 22 : 14;
    }
    if (bot.blockTicks > 0) {
      input.block = true;
      attack = false;
      if (threat) {
        aimX = threat.x - px;
        aimY = threat.y - py;
      }
    } else if (
      target &&
      !ranged &&
      bot.reactTicks === 0 &&
      Math.hypot(target.x - px, target.y - py) < 1.6
    ) {
      const tc = target.e.get(Combat);
      if (tc?.punchActive) {
        bot.reactTicks = Math.round(24 + (1 - bot.skill) * 48);
        if (rng.next() < bot.skill * (bot.style === 1 ? 0.55 : 0.3)) bot.blockTicks = 8;
      }
    }

    // --- Emit the frame's input. Jumps are pulsed so the controller sees rising edges; airborne
    // jump presses are only allowed when deliberately wall-climbing, otherwise a bot brushing a
    // ledge would wall-kick itself off the stage.
    const wallTouch =
      !ctrl.grounded &&
      moveX !== 0 &&
      !!raycastClosest(
        world,
        px,
        py + 0.15,
        px + Math.sign(moveX) * t.wallDetectDistance,
        py + 0.15,
        skip,
      );
    const tallWall =
      wallTouch &&
      !!raycastClosest(
        world,
        px,
        py + 1.6,
        px + Math.sign(moveX) * (t.wallDetectDistance + 0.2),
        py + 1.6,
        skip,
      );
    input.moveX = clamp1(moveX);
    if (ctrl.grounded || ctrl.coyote > 0) input.jump = wantJump && bot.think % 2 === 0;
    // Outside the arena a short ledge is still our escape: requiring a tall wall can leave a
    // bot sliding forever just below its lip, out of reach of the final opponent.
    else input.jump = wantJump && climbing && (offStage ? wallTouch : tallWall) && bot.think % 6 === 0;
    input.attack = attack;
    const alen = Math.hypot(aimX, aimY);
    input.aimX = alen > 1e-6 ? aimX / alen : ctrl.facing;
    input.aimY = alen > 1e-6 ? aimY / alen : 0;

    ctx.inputs[player.inputIndex] = input;
    ctx.rawInputs[player.inputIndex] = { ...input };
    bot.engaged = engaged;
    if (engaged) bot.engagementGrace = 45;
    entity.set(Bot, bot);
  });
}
